/**
 * TanStack QueryClient singleton for the Sales Coach dashboard SPA.
 *
 * D-30 defaults:
 *   - staleTime 60s       — usage metrics don't change minute-to-minute.
 *   - retry 1             — one silent retry for transient network blips.
 *   - retryOnMount false  — don't storm the backend when we re-render the page.
 *   - refetchOnWindowFocus false — admin dashboards don't need aggressive refresh.
 *
 * D-31: <App> mounts <QueryClientProvider client={queryClient}> + devtools.
 *
 * 401 bridge: apiFetch (services/api.ts) calls `onUnauthorized` on 401 HTTP
 * responses. We register a handler in the App root (Plan 04-09) that:
 *   1. clearAuth() — drops the localStorage token,
 *   2. navigate('/login') — via react-router-dom.
 * Exposed via setUnauthorizedHandler / triggerUnauthorized so non-React
 * modules (query mutations, deep error handlers) can trigger the same flow.
 */

import { QueryClient } from '@tanstack/react-query';

import { setUnauthorizedHandler as setApiUnauthorizedHandler } from '@/services/api';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: 1,
      retryOnMount: false,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

let handler: (() => void) | null = null;

/**
 * Register the 401 handler (called by App root once router + auth are mounted).
 * Passing the same reference to api.ts keeps both layers in sync — the apiFetch
 * wrapper and direct callers (e.g. manual mutations) converge on one flow.
 */
export function setUnauthorizedHandler(fn: () => void): void {
  handler = fn;
  setApiUnauthorizedHandler(fn);
}

/** Manually trigger the unauthorized flow (non-React callers). */
export function triggerUnauthorized(): void {
  if (handler) handler();
}

/** Unregister the handler — called on unmount / logout. */
export function clearUnauthorizedHandler(): void {
  handler = null;
  setApiUnauthorizedHandler(null);
}
